import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { useNotifications } from 'hooks/useNotifications';
import { useAuth } from 'contexts/AuthContext';
import Icon from 'components/AppIcon';

const NotificationCenter = () => {
  const { user, profile } = useAuth();
  const userRole = profile?.is_admin ? 'admin' : profile?.is_writer ? 'writer' : profile?.is_qa ? 'qa' : 'client';
  const { notifications, markAsRead, markAllAsRead, deleteNotification } = useNotifications(user?.uid, userRole);
  const filteredNotifications = notifications;

  const unreadCount = notifications.filter(n => !n.read_by?.includes(user?.uid)).length;

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'comment':
        return 'MessageCircle';
      case 'assignment':
        return 'FileText';
      case 'class':
        return 'GraduationCap';
      case 'system':
        return 'Bell';
      default:
        return 'Info';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'comment':
        return 'text-blue-400';
      case 'assignment':
        return 'text-green-400';
      case 'class':
        return 'text-purple-400';
      case 'system':
        return 'text-yellow-400';
      default:
        return 'text-gray-400';
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read_by?.includes(user?.uid)) {
      await markAsRead(notification.id);
    }
    // Navigate to the relevant item/page
    // This would be implemented based on your routing system
  };

  const formatNotificationTime = (date) => {
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return format(date, 'MMM dd');
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Icon name="Activity" size={20} className="text-white/80" />
            <h2 className="text-white font-semibold">Activity & Notifications</h2>
            {unreadCount > 0 && (
              <span className="bg-primary text-white text-xs px-2 py-1 rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="text-primary hover:text-primary/80 text-sm font-medium transition-colors"
            >
              Mark all read
            </button>
          )}
        </div>

      </div>

      {/* Notifications List */}
      <div className="flex-1 overflow-y-auto">
        {filteredNotifications.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Activity" size={48} className="text-white/20 mx-auto mb-2" />
            <p className="text-white/60">No activity yet</p>
            <p className="text-white/40 text-sm">
              Activity and updates will appear here
            </p>
          </div>
        ) : (
          <div className="p-2 space-y-1">
            <AnimatePresence>
              {filteredNotifications.map((notification) => {
                const isUnread = !notification.read_by?.includes(user?.uid);
                
                return (
                  <motion.div
                    key={notification.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className={`p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                      isUnread
                        ? 'bg-primary/10 border border-primary/30 hover:bg-primary/20'
                        : 'bg-white/5 border border-white/10 hover:bg-white/10'
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isUnread ? 'bg-primary/20' : 'bg-white/10'
                      }`}>
                        <Icon 
                          name={getNotificationIcon(notification.type)} 
                          size={14} 
                          className={isUnread ? 'text-primary' : getNotificationColor(notification.type)} 
                        />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h4 className={`text-sm font-medium ${
                            isUnread ? 'text-white' : 'text-white/90'
                          } leading-tight`}>
                            {notification.title}
                          </h4>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {isUnread && (
                              <div className="w-2 h-2 bg-primary rounded-full"></div>
                            )}
                            <span className="text-white/40 text-xs">
                              {formatNotificationTime(notification.created_at)}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteNotification(notification.id);
                              }}
                              className="w-4 h-4 hover:bg-red-500/20 hover:text-red-400 text-white/40 rounded transition-colors flex items-center justify-center"
                              title="Delete notification"
                            >
                              <Icon name="X" size={12} />
                            </button>
                          </div>
                        </div>

                        <p className={`text-xs leading-relaxed ${
                          isUnread ? 'text-white/80' : 'text-white/60'
                        } line-clamp-2`}>
                          {notification.body}
                        </p>

                        {/* Metadata */}
                        {notification.metadata && (
                          <div className="flex items-center gap-2 mt-2">
                            {notification.metadata.author_name && (
                              <div className="flex items-center gap-1">
                                <Icon name="User" size={10} className="text-white/40" />
                                <span className="text-white/40 text-xs">
                                  {notification.metadata.author_name}
                                </span>
                              </div>
                            )}
                            {notification.metadata.item_title && (
                              <div className="flex items-center gap-1">
                                <Icon name="Link" size={10} className="text-white/40" />
                                <span className="text-white/40 text-xs truncate max-w-24">
                                  {notification.metadata.item_title}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="p-4 border-t border-white/10 bg-white/5">
        <div className="flex justify-between items-center">
          <span className="text-white/60 text-xs">
            {notifications.length} activity items
          </span>
          <div className="flex items-center space-x-2">
            <button 
              onClick={() => {
                // Clear all notifications
                filteredNotifications.forEach(n => deleteNotification(n.id));
              }}
              className="text-white/60 hover:text-red-400 transition-colors"
              title="Clear all"
            >
              <Icon name="Trash2" size={14} />
            </button>
            <button className="text-white/60 hover:text-white/80 transition-colors" title="Settings">
              <Icon name="Settings" size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationCenter;